import { cn } from '@/lib/cn';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { ChatListItem } from '@/types';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

const EASE_OUT_QUAD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];
const ROW_ENTER_DURATION = 0.2;
const ROW_EXIT_DURATION = 0.16;
const SECTION_ENTER_DURATION = 0.22;
const SECTION_EXIT_DURATION = 0.18;

function SavedChatsAnimatedList({
  chats,
  children,
  shouldReduceMotion,
}: {
  chats: ChatListItem[];
  children: (chat: ChatListItem) => ReactNode;
  shouldReduceMotion: boolean | null;
}) {
  return (
    <m.div layout className="flex flex-col gap-0.5">
      <AnimatePresence initial={false} mode="popLayout">
        {chats.map((chat) => (
          <m.div
            key={chat.id}
            layout={!shouldReduceMotion}
            initial={shouldReduceMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: {
                duration: shouldReduceMotion ? 0 : ROW_EXIT_DURATION,
                ease: EASE_OUT_QUAD,
              },
              y: {
                duration: shouldReduceMotion ? 0 : ROW_ENTER_DURATION,
                ease: EASE_OUT_QUAD,
              },
              layout: shouldReduceMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 320, damping: 32 },
            }}
          >
            {children(chat)}
          </m.div>
        ))}
      </AnimatePresence>
    </m.div>
  );
}

/**
 * Chat-sidebar "Saved" section — starred chats for authenticated users.
 * Hidden until the saved-chats query settles and at least one chat matches
 * (including search filtering).
 */
export function SavedChatsSidebarSection({
  isAuthenticated,
  filteredSavedChats,
  savedChatsPending,
  children,
}: {
  isAuthenticated: boolean;
  filteredSavedChats: ChatListItem[];
  savedChatsPending: boolean;
  children: (chat: ChatListItem) => ReactNode;
}): JSX.Element | null {
  const [sectionOpen, setSectionOpen] = useState(true);
  const shouldReduceMotion = useReducedMotion();

  // Dynamic section: hide while saved chats load, or when none match (incl. search).
  // Gate on savedChatsPending so an empty filter doesn't flash the section away
  // before the fetch settles.
  if (!isAuthenticated || savedChatsPending || filteredSavedChats.length === 0) {
    return null;
  }

  const animatedList = (
    <SavedChatsAnimatedList
      chats={filteredSavedChats}
      shouldReduceMotion={shouldReduceMotion}
    >
      {children}
    </SavedChatsAnimatedList>
  );

  return (
    <AnimatePresence initial={false}>
      <m.div
        key="saved-chats-section"
        initial={shouldReduceMotion ? false : { opacity: 0, height: 0, y: -6 }}
        animate={{ opacity: 1, height: 'auto', y: 0 }}
        exit={
          shouldReduceMotion ? { opacity: 0 } : { opacity: 0, height: 0, y: -4 }
        }
        transition={{
          opacity: {
            duration: shouldReduceMotion ? 0 : SECTION_EXIT_DURATION,
            ease: EASE_OUT_QUAD,
          },
          y: {
            duration: shouldReduceMotion ? 0 : SECTION_ENTER_DURATION,
            ease: EASE_OUT_QUAD,
          },
          height: shouldReduceMotion
            ? { duration: 0 }
            : { type: 'spring', stiffness: 280, damping: 34, mass: 0.9 },
        }}
        className="overflow-hidden"
      >
        <Collapsible open={sectionOpen} onOpenChange={setSectionOpen}>
          <CollapsibleTrigger className="group/saved flex w-full items-center justify-between p-2 pb-1">
            <TypographyCaption className="font-medium text-text-tertiary">
              <FormattedMessage
                defaultMessage="Pinned"
                id="g2xrw73eRK"
                description="Chat sidebar section heading for pinned chats"
              />
            </TypographyCaption>
            <ChevronDown
              size={14}
              className={cn(
                'text-text-tertiary opacity-0 transition-[transform,opacity] duration-200 ease-out group-hover/saved:opacity-100',
                sectionOpen && '-rotate-180'
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>{animatedList}</CollapsibleContent>
        </Collapsible>
      </m.div>
    </AnimatePresence>
  );
}
